// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '../../../constants/appointmentTypes';

export interface SweRow {
  id: string;
  name: string;
  email: string;
}

export interface UseSweListResult {
  rows: SweRow[];
  loading: boolean;
  error: string | undefined;
  refresh: () => void;
  reload: () => void;
}

const PRACTITIONER_PAGE_SIZE = 500;

export function useSweList(): UseSweListResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<SweRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [fetchKey, setFetchKey] = useState(0);
  const noCacheRef = useRef(false);

  const refresh = useCallback(() => {
    noCacheRef.current = false;
    setFetchKey((k) => k + 1);
  }, []);

  const reload = useCallback(() => {
    noCacheRef.current = true;
    setFetchKey((k) => k + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchKey is an intentional trigger counter
  useEffect(() => {
    let active = true;
    const noCache = noCacheRef.current;
    noCacheRef.current = false;

    setLoading(true);
    setError(undefined);

    async function load(): Promise<void> {
      const searchOptions = noCache ? { cache: 'no-cache' as const } : undefined;

      // Filter PractitionerRole server-side by the SWE role token. Fetching the
      // first N rows and filtering client-side silently drops freshly-created
      // SWE roles once the project holds more than N PractitionerRole resources
      // — the new role falls outside the page. The `role` search parameter maps
      // to PractitionerRole.code and is indexed, so let the server do the filter.
      const roles = await medplum.searchResources(
        'PractitionerRole',
        {
          role: `${POLARIS_PROVIDER_ROLE_SYSTEM}|software-engineer`,
          _count: String(PRACTITIONER_PAGE_SIZE),
        },
        searchOptions
      );

      // Collect practitioner IDs
      const practitionerIds = new Set<string>();
      for (const role of roles) {
        const ref = role.practitioner?.reference;
        if (ref) {
          const id = ref.split('/')[1];
          if (id) {
            practitionerIds.add(id);
          }
        }
      }

      if (practitionerIds.size === 0) {
        if (!active) {
          return;
        }
        setRows([]);
        return;
      }

      // Fetch the practitioners; skip any that have been deleted (404/410)
      const results = await Promise.allSettled(
        Array.from(practitionerIds).map((id) => medplum.readResource('Practitioner', id))
      );
      const practitioners = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => (r as PromiseFulfilledResult<Practitioner>).value);

      const nextRows = practitioners
        .map((p: Practitioner) => {
          const id = p.id ?? '';
          const name = [p.name?.[0]?.given?.join(' '), p.name?.[0]?.family].filter(Boolean).join(' ') || 'Unknown';
          const email = p.telecom?.find((t) => t.system === 'email')?.value ?? '';
          return { id, name, email };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      console.debug('[useSweList] loaded', {
        practitionerRoleCount: roles.length,
        sweCount: nextRows.length,
        names: nextRows.map((r) => r.name),
      });

      if (!active) {
        return;
      }
      setRows(nextRows);
    }

    load()
      .catch((err) => {
        console.error('[useSweList] Failed to load SWE list:', err);
        if (active) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, fetchKey]);

  return { rows, loading, error, refresh, reload };
}
